import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LandingSitePublishedFormsDtoOut } from '@/models/out/LandingSitePublishedFormsDtoOut';
import { downloadFileFromApi } from '@/helpers/downloadFile';
import { formatDateLocale } from '@/helpers/dateHelper';
import externalLinkIcon from '@/assets/icons/external-link.svg';
import DocumentRow from './DocumentRow';
import styles from '../FundSettings.module.css';

/**
 * The organization's approvals, in the order an accountant asks for them. Kept in step with the
 * File entries of LandingSiteFormsCatalog.
 */
const APPROVAL_ENTRIES = ['proper-management', 'tax-exemption-46', 'tax-deduction-at-source', 'account-ownership', 'public-bodies'];

/** Where the API serves an entry's file from, as an attachment. */
const fileUrlOf = (key: string) => `landing-site-forms/${key}/file`;

interface ApprovalsSectionProps {
  forms: LandingSitePublishedFormsDtoOut | undefined;
}

/**
 * «לרואה החשבון: אישורי העמותה» (HAT-1231). The same files the landing site serves, from the same
 * managed catalogue: when the office replaces one, it changes here at the same moment, so what a
 * coordinator forwards from this screen is always the current copy. Each row says when the office
 * last published it - the question an accountant actually asks about an approval - and offers
 * «צפייה», «הורדה» and the copy control, like every other document row.
 */
const ApprovalsSection = ({ forms }: ApprovalsSectionProps) => {
  const { t } = useTranslation();
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const downloadEntry = async (key: string, label: string) => {
    if (downloadingKey) return;
    setDownloadingKey(key);
    try {
      await downloadFileFromApi(fileUrlOf(key), `${label}.pdf`, '');
    } finally {
      setDownloadingKey(null);
    }
  };

  const guidestarUrl = forms?.links?.guidestar;

  return (
    <div className={styles.panel}>
      <h2 className={styles.panelTitle}>{t('fundSettingsDocumentsPage.approvalsTitle')}</h2>
      <p className={styles.panelSubtitle}>{t('fundSettingsDocumentsPage.approvalsSubtitle')}</p>

      <div className={styles.documentList}>
        {APPROVAL_ENTRIES.map((key) => {
          const url = forms?.links?.[key];
          if (!url) return null;
          const label = t(`landingSiteForms.items.${key}`);
          const publishedAt = forms?.publishedAt?.[key];
          return (
            <DocumentRow
              key={key}
              name={label}
              note={publishedAt ? t('fundSettingsDocumentsPage.publishedAt', { date: formatDateLocale(publishedAt) }) : undefined}
              href={url}
              onDownload={() => downloadEntry(key, label)}
              isBusy={downloadingKey === key}
              copyValue={url}
            />
          );
        })}
      </div>

      {guidestarUrl && (
        <p className={styles.panelSubtitle}>
          {t('fundSettingsDocumentsPage.publicRegister')}{' '}
          <a href={guidestarUrl} target="_blank" rel="noreferrer" className={styles.factLink}>
            {t('fundSettingsDocumentsPage.guidestarPage')}
            <img src={externalLinkIcon} alt="" className={styles.actionIcon} />
          </a>
        </p>
      )}
    </div>
  );
};

export default ApprovalsSection;
